import React, {useState} from 'react';
import {Pressable, ScrollView, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {S, T} from '../courseTheme';
import {E} from './editorTheme';
import type {RailRow} from './editorLogic';
import {focusVisible, IconButton, motion, ring, StatusMark} from './ui';

type Props = {
  rows: RailRow[];
  /** Rows before the filter, for the count. */
  total: number;
  needsReviewCount: number;
  currentPageId: number | null;
  needsReviewOnly: boolean;
  onToggleFilter: () => void;
  onOpen: (pageId: number) => void;
  /** Hide the rail (desktop) or close the drawer (narrow). */
  onClose: () => void;
  closeLabel: string;
  reduced: boolean;
};

function Row({
  row,
  current,
  onOpen,
  reduced,
}: {
  row: RailRow;
  current: boolean;
  onOpen: () => void;
  reduced: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{selected: current}}
      accessibilityLabel={`Open chapter ${row.number}: ${row.title}, ${row.status.word}`}
      onPress={onOpen}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: S[3],
          minHeight: 56,
          paddingVertical: S[2] + 2,
          paddingLeft: S[4] - 3,
          paddingRight: S[4],
          // Selection is a bar and a weight as well as a tint, so it never
          // rests on colour.
          borderLeftWidth: 3,
          borderLeftColor: current ? E.accent : 'transparent',
          backgroundColor: current
            ? E.selected
            : pressed
            ? E.pressed
            : hovered
            ? E.hover
            : 'transparent',
          cursor: 'pointer',
        } as any,
        ring(focused, true),
        motion(reduced),
      ]}>
      <CCText
        style={{
          width: 22,
          fontSize: T.sm,
          fontWeight: '600',
          color: E.muted,
          fontVariant: ['tabular-nums'],
          lineHeight: 20,
          textAlign: 'right',
        }}>
        {String(row.number)}
      </CCText>
      <View style={{flex: 1, gap: 2}}>
        <CCText
          numberOfLines={2}
          style={{
            fontSize: T.sm + 1,
            fontWeight: current ? '700' : '500',
            color: E.ink,
            lineHeight: 20,
          }}>
          {row.title}
        </CCText>
        <StatusMark status={row.status} />
      </View>
    </Pressable>
  );
}

/**
 * Every chapter of the course, as a list the reviewer works down: number,
 * neutral title, and a status in words. The only control above it is the
 * "Needs review" filter — the rail should read as a table of contents, not a
 * dashboard.
 */
export default function ChapterRail({
  rows,
  total,
  needsReviewCount,
  currentPageId,
  needsReviewOnly,
  onToggleFilter,
  onOpen,
  onClose,
  closeLabel,
  reduced,
}: Props) {
  const [filterFocused, setFilterFocused] = useState(false);
  return (
    <View
      accessibilityLabel="Chapters"
      style={{flex: 1, minHeight: 0, backgroundColor: E.surface}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingLeft: S[4],
          paddingRight: S[1],
          paddingTop: S[2],
          gap: S[2],
        }}>
        <CCText
          accessibilityRole="header"
          style={{flex: 1, fontSize: T.base, fontWeight: '700', color: E.ink}}>
          {`Chapters · ${total}`}
        </CCText>
        <IconButton icon="chevron-back" label={closeLabel} onPress={onClose} />
      </View>
      <View style={{paddingHorizontal: S[4], paddingBottom: S[2]}}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Needs review"
          accessibilityState={{selected: needsReviewOnly}}
          onPress={onToggleFilter}
          onFocus={() => setFilterFocused(focusVisible())}
          onBlur={() => setFilterFocused(false)}
          style={({pressed}) => [
            {
              alignSelf: 'flex-start',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              minHeight: 44,
              paddingHorizontal: S[3],
              borderRadius: 999,
              borderWidth: 1,
              borderColor: needsReviewOnly ? E.accent : 'rgba(38,35,34,0.22)',
              backgroundColor: needsReviewOnly
                ? E.selected
                : pressed
                ? E.pressed
                : E.surface,
              cursor: 'pointer',
            } as any,
            ring(filterFocused),
          ]}>
          <Ionicons
            name={needsReviewOnly ? 'checkmark' : 'funnel-outline'}
            size={15}
            color={needsReviewOnly ? E.accent : E.ink}
          />
          <CCText
            style={{
              fontSize: T.sm,
              fontWeight: '600',
              color: needsReviewOnly ? E.accent : E.ink,
            }}>
            {`Needs review · ${needsReviewCount}`}
          </CCText>
        </Pressable>
      </View>
      <ScrollView
        style={{flex: 1}}
        contentContainerStyle={{paddingBottom: S[4]}}>
        {rows.length ? (
          rows.map(r => (
            <Row
              key={r.chapterId}
              row={r}
              current={r.pageId === currentPageId}
              onOpen={() => onOpen(r.pageId)}
              reduced={reduced}
            />
          ))
        ) : (
          <View style={{padding: S[4], gap: S[1]}}>
            <CCText style={{fontSize: T.sm, fontWeight: '600', color: E.ink}}>
              Nothing left to review.
            </CCText>
            <CCText style={{fontSize: T.sm, color: E.muted, lineHeight: 19}}>
              Every chapter has been opened and nothing is flagged.
            </CCText>
          </View>
        )}
      </ScrollView>
    </View>
  );
}
